import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useGoTo } from "@/lib/useGoTo";
import { Mark } from "./Mark";

const LINKS = [
  ["behandlungen", "Behandlungen"],
  ["/preisliste", "Preisliste"],
  ["ueber-mich", "Über mich"],
  ["termin", "Terminanfrage"],
  ["kontakt", "Kontakt"],
];

const NavItem = ({ target, label, goTo, onDone, className }) => {
  const testId = `nav-link-${target.replace("/", "")}`;
  if (target.startsWith("/"))
    return <Link to={target} data-testid={testId} onClick={onDone} className={className}>{label}</Link>;
  return (
    <a href={`/#${target}`} data-testid={testId} className={className} onClick={(e) => { e.preventDefault(); onDone?.(); goTo(target); }}>
      {label}
    </a>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goTo = useGoTo();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-cream/90 backdrop-blur-xl shadow-[0_1px_0_rgba(197,155,39,0.25)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-5 sm:px-8">
        <Link to="/" data-testid="nav-brand" className="flex min-w-0 items-center gap-2.5" aria-label="Maison Princess – Startseite">
          <Mark className="h-8 w-8 shrink-0" />
          <span className="whitespace-nowrap font-serif text-[13px] tracking-[0.14em] text-ink sm:text-[15px] sm:tracking-[0.28em]">MAISON PRINCESS</span>
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Hauptnavigation">
          {LINKS.map(([t, l]) => (
            <NavItem key={t} target={t} label={l} goTo={goTo} className={`link-underline text-[13px] tracking-wide hover:text-ink ${pathname === t ? "text-gold-deep" : "text-ink-soft"}`} />
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-2">
          <button
            data-testid="nav-booking-btn"
            onClick={() => goTo("termin")}
            className="btn-gold rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] sm:px-6 sm:py-2.5"
          >
            Termin<span className="hidden sm:inline"> anfragen</span>
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            className="grid h-10 w-10 place-items-center rounded-full border border-gold/40 text-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-gold/20 lg:hidden"
            aria-label="Mobile Navigation"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map(([t, l]) => (
                <NavItem key={t} target={t} label={l} goTo={goTo} onDone={() => setOpen(false)} className="border-b border-beige py-3.5 font-serif text-2xl text-ink last:border-b-0" />
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
